import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  ChevronRight,
  ChevronsRight,
  Minus,
  Mic,
  UserPlus,
} from 'lucide-react';
import { useAppointments } from '@/hooks/useAppointments';
import { useRecordingSessionStore } from '@/stores/useRecordingSessionStore';
import { useDockLayoutStore } from '@/stores/useDockLayoutStore';
import { pickNextAppointment } from '@/utils/nextAppointment';

/**
 * Dock-shaped preview of the most-imminent appointment, shown when
 * there's no active recording session. Reuses the existing dock real
 * estate (right rail on desktop, slim mini-bar on mobile) so the
 * surface that's about to fill with live capture already shows the
 * doctor what's next + a one-tap Start.
 *
 * Visibility window: ±90 minutes around the appointment time. Outside
 * that, the dock returns null so the page chrome breathes again.
 *
 * Renders nothing for cancelled/completed/no-show appointments; the
 * "next" pick walks past them to find a meaningful row.
 */

interface Props {
  isRTL: boolean;
}

const VISIBILITY_BEFORE_MIN = 90;
// How long after the scheduled time a missed appointment can still take
// the dock. Capped tight so a 2-hour-old no-show doesn't sit on every
// page pretending to be next-up. If the patient is genuinely late and
// in the room, the doctor starts capture and the RecordingDock takes
// over the same surface — the late row stops mattering.
const VISIBILITY_AFTER_MIN = 15;
const ALMOST_DUE_MIN = 15;

const todayStr = (now: Date): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const useNow = (intervalMs = 30_000) => {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

export const NextAppointmentDock: React.FC<Props> = ({ isRTL }) => {
  const navigate = useNavigate();
  const armSession = useRecordingSessionStore((s) => s.armSession);
  // Hide entirely while a recording is in progress — the RecordingDock
  // owns that same surface (right rail / bottom sheet) during capture.
  // Reading the slice directly subscribes to changes, so the dock
  // swap is automatic when the session starts/ends.
  const activeSession = useRecordingSessionStore((s) => s.session);
  const rightRailCollapsed = useDockLayoutStore((s) => s.rightRailCollapsed);
  const setRightRailCollapsed = useDockLayoutStore(
    (s) => s.setRightRailCollapsed,
  );
  const bottomBarCollapsed = useDockLayoutStore((s) => s.bottomBarCollapsed);
  const setBottomBarCollapsed = useDockLayoutStore(
    (s) => s.setBottomBarCollapsed,
  );
  const setBottomHeight = useDockLayoutStore((s) => s.setBottomHeight);
  const now = useNow(30_000);
  const today = useMemo(() => todayStr(now), [now]);

  const { data } = useAppointments({
    limit: 100,
    startDate: today,
    endDate: today,
  });

  const appointments = useMemo(() => data?.data ?? [], [data]);

  // Compute the "imminent" pick via the shared helper so the wake-word
  // "MAIE start next" dispatch and this dock pick from the same logic
  // (different windows: the dock is bounded to keep the right rail
  // breathing, voice resolution is window-less).
  const next = useMemo(
    () =>
      pickNextAppointment(appointments, now, {
        windowBeforeMin: VISIBILITY_BEFORE_MIN,
        windowAfterMin: VISIBILITY_AFTER_MIN,
      }),
    [appointments, now],
  );

  // Effect MUST run before any conditional return so the hook order
  // stays stable. Reports our visible mobile-bar height (or 0 when not
  // shown) into the layout store so the launcher pill can offset.
  // The full bar's rendered height is ~64 px (padding + icon row); the
  // collapsed corner pill is ~44 px. Gap is added on the consumer side.
  const isMobileBarVisible = Boolean(next) && !activeSession;
  const mobileBarHeight = bottomBarCollapsed ? 44 : 64;
  useEffect(() => {
    setBottomHeight(
      'next-appointment',
      isMobileBarVisible ? mobileBarHeight : 0,
    );
    return () => {
      setBottomHeight('next-appointment', 0);
    };
  }, [isMobileBarVisible, mobileBarHeight, setBottomHeight]);

  if (activeSession) return null;
  if (!next) return null;

  const patient = (next as typeof next & {
    patient?: { firstName?: string; lastName?: string };
  }).patient;
  const patientName =
    `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim() ||
    (isRTL ? 'مريض' : 'Patient');

  const targetDate = new Date(now);
  if (next.time) {
    const [hh, mm] = next.time.split(':').map((n) => parseInt(n, 10));
    if (!Number.isNaN(hh) && !Number.isNaN(mm)) {
      targetDate.setHours(hh, mm, 0, 0);
    }
  }
  const diffMin = Math.round((targetDate.getTime() - now.getTime()) / 60000);

  const isLate = diffMin < -1;
  const isImminent = !isLate && diffMin <= ALMOST_DUE_MIN;

  const countdown = (() => {
    if (diffMin > 60) {
      return isRTL
        ? `خلال ${Math.round(diffMin / 60)} ساعة`
        : `in ${Math.round(diffMin / 60)} h`;
    }
    if (diffMin > 1) return isRTL ? `خلال ${diffMin} د` : `in ${diffMin} min`;
    if (diffMin >= -1) return isRTL ? 'يبدأ الآن' : 'starting now';
    const late = -diffMin;
    if (late <= 60) return isRTL ? `متأخر ${late} د` : `${late} min late`;
    return isRTL ? `متأخر ${Math.round(late / 60)} ساعة` : `${Math.round(late / 60)} h late`;
  })();

  const handleStart = () => {
    if (!next.patientId) return;
    // Open the cold-mic arm panel (patient + appointment pre-filled) instead
    // of starting hot. The appointment id rides along so the produced
    // medical_record links back to this calendar tile.
    armSession({
      patientId: next.patientId,
      patientNameEn: patientName,
      appointmentId: next.id,
    });
  };

  // Tap on the row body (patient name / time / countdown) → view patient.
  // Recording only happens when the user taps the explicit Start CTA.
  // Same split as the Today's Appointments rows on /home, so the two
  // surfaces behave consistently.
  const handleViewPatient = () => {
    if (!next.patientId) return;
    navigate(`/patients/${next.patientId}`);
  };

  const accent = isLate
    ? 'var(--color-warning)'
    : isImminent
      ? 'var(--color-primary-600)'
      : 'var(--color-text-tertiary)';
  const tint = isLate
    ? 'color-mix(in srgb, var(--color-warning) 12%, var(--color-bg-primary))'
    : isImminent
      ? 'color-mix(in srgb, var(--color-primary-600) 8%, var(--color-bg-primary))'
      : 'var(--color-bg-primary)';

  return (
    <>
      {/* Desktop right rail — same width as RecordingDock so the
          surface stays consistent when the doctor presses Start.
          Doctors who want the page width back can collapse it via the
          double-chevron in the top corner; the choice persists across
          reloads via useDockLayoutStore. Collapsed state shows a
          12 px sliver with an expand button so the dock isn't
          permanently lost. */}
      {rightRailCollapsed ? (
        <aside
          className="hidden lg:flex flex-col items-center justify-start w-10 flex-shrink-0 py-3"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderInlineStart: '1px solid var(--color-border-primary)',
          }}
          aria-label={isRTL ? 'الموعد القادم (مطوي)' : 'Next appointment (collapsed)'}
        >
          <button
            type="button"
            onClick={() => setRightRailCollapsed(false)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors"
            style={{
              color: 'var(--color-text-secondary)',
              backgroundColor: 'transparent',
            }}
            title={isRTL ? 'إظهار شريط الموعد القادم' : 'Show next-appointment dock'}
            aria-label={isRTL ? 'إظهار' : 'Expand'}
          >
            <ChevronsRight
              className="w-4 h-4"
              style={{
                transform: isRTL ? 'rotate(0)' : 'rotate(180deg)',
              }}
            />
          </button>
          <Calendar
            className="w-4 h-4 mt-2"
            style={{ color: accent }}
          />
        </aside>
      ) : (
      <aside
        className="hidden lg:flex flex-col w-[460px] xl:w-[520px] flex-shrink-0 relative"
        style={{
          backgroundColor: tint,
          borderInlineStart: '1px solid var(--color-border-primary)',
        }}
        aria-label={isRTL ? 'الموعد القادم' : 'Next appointment'}
      >
        <button
          type="button"
          onClick={() => setRightRailCollapsed(true)}
          className="absolute top-3 end-3 inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors"
          style={{
            color: 'var(--color-text-tertiary)',
            backgroundColor: 'transparent',
          }}
          title={isRTL ? 'طي الشريط' : 'Collapse dock'}
          aria-label={isRTL ? 'طي' : 'Collapse'}
        >
          <ChevronsRight
            className="w-4 h-4"
            style={{
              transform: isRTL ? 'rotate(180deg)' : 'none',
            }}
          />
        </button>
        <div className="flex flex-col flex-1 p-5 gap-4">
          <div
            className="inline-flex items-center gap-2"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">
              {isRTL ? 'الموعد القادم' : 'Next up'}
            </span>
          </div>
          <div>
            <div
              className="text-3xl font-semibold tabular-nums tracking-tight"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {next.time?.slice(0, 5) ?? '—:—'}
            </div>
            <div
              className="text-xs font-semibold uppercase tracking-wide mt-1"
              style={{ color: accent }}
            >
              {countdown}
            </div>
          </div>
          <div>
            <div
              className="text-base font-semibold truncate"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {patientName}
            </div>
            <div
              className="text-xs mt-0.5 truncate"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {next.reason || (isRTL ? 'استشارة' : 'Consult')}
              {next.duration ? ` · ${next.duration} ${isRTL ? 'د' : 'min'}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={handleStart}
            className="mt-auto inline-flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all hover:shadow-md"
            style={{
              backgroundColor: 'var(--color-primary-600)',
              color: 'white',
            }}
            title={isRTL ? '«MAIE ابدأ التالي»' : '“MAIE, start next”'}
          >
            <Mic className="w-4 h-4" />
            {isRTL ? 'ابدأ التالي' : 'Start next'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/patients/${next.patientId}`)}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-medium"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <UserPlus className="w-3.5 h-3.5" />
            {isRTL ? 'فتح ملف المريض' : 'Open patient chart'}
            <ChevronRight
              className="w-3.5 h-3.5"
              style={{
                transform: isRTL ? 'rotate(180deg)' : 'none',
              }}
            />
          </button>
        </div>
      </aside>
      )}

      {/* Mobile slim sticky bar — bottom-anchored, single row.
          Tap targets are split: the row body opens the patient chart,
          the explicit Start pill begins the recording. A single
          full-row tap that auto-starts is too easy to fire by accident
          (the bar covers the bottom of the screen on every page) and
          was leaving stale recording sessions behind.

          The full-width bar covers the bottom of every page, which can
          sit over a page's primary CTAs (Save / Cancel). A minimize
          control shrinks it to a small corner pill so those CTAs are
          reachable; the choice persists across reloads. */}
      {bottomBarCollapsed ? (
        <button
          type="button"
          onClick={() => setBottomBarCollapsed(false)}
          className="lg:hidden fixed bottom-3 end-3 z-30 inline-flex items-center justify-center w-11 h-11 rounded-full"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            border: `1px solid ${accent}`,
            color: accent,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
          dir={isRTL ? 'rtl' : 'ltr'}
          aria-label={isRTL ? 'إظهار الموعد القادم' : 'Show next appointment'}
          title={isRTL ? 'إظهار الموعد القادم' : 'Show next appointment'}
        >
          <Calendar className="w-5 h-5" />
        </button>
      ) : (
      <div
        className="lg:hidden fixed inset-x-3 bottom-3 z-30 flex items-center gap-2 rounded-xl px-3 py-2.5"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          border: `1px solid ${accent}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <button
          type="button"
          onClick={handleViewPatient}
          className="flex items-center gap-3 flex-1 min-w-0 text-start py-1 rounded-md"
          aria-label={isRTL ? 'فتح ملف المريض للموعد القادم' : 'Open chart for next patient'}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
            style={{
              backgroundColor: `color-mix(in srgb, ${accent} 16%, transparent)`,
              color: accent,
            }}
          >
            <Calendar className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[10px] uppercase tracking-wide font-semibold" style={{ color: accent }}>
              {countdown} · {next.time?.slice(0, 5) ?? '—'}
            </span>
            <span
              className="block text-sm font-semibold truncate"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {patientName}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={handleStart}
          className="inline-flex items-center justify-center px-3 py-2 rounded-md text-xs font-semibold flex-shrink-0"
          style={{
            backgroundColor: 'var(--color-primary-600)',
            color: 'white',
          }}
          aria-label={isRTL ? 'ابدأ التالي' : 'Start next'}
          title={isRTL ? '«MAIE ابدأ التالي»' : '“MAIE, start next”'}
        >
          <Mic className="w-3.5 h-3.5 me-1.5" />
          {isRTL ? 'التالي' : 'Next'}
        </button>
        <button
          type="button"
          onClick={() => setBottomBarCollapsed(true)}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md flex-shrink-0"
          style={{ color: 'var(--color-text-tertiary)' }}
          aria-label={isRTL ? 'تصغير' : 'Minimize'}
          title={isRTL ? 'تصغير' : 'Minimize'}
        >
          <Minus className="w-4 h-4" />
        </button>
      </div>
      )}
    </>
  );
};
